import {
  createContext,
  useContext,
  useCallback,
  type ReactNode,
} from 'react'
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  fetchWishlist,
  addToWishlist,
  removeFromWishlist,
  type WishlistItem,
} from '../api/wishlist'
import { useAuth } from './AuthContext'

// ── Types ────────────────────────────────────────────────────────────────────

interface WishlistContextValue {
  items: WishlistItem[]
  isLoading: boolean
  isInWishlist: (normalizedMpn: string) => boolean
  addItem: (normalizedMpn: string) => void
  removeItem: (uuid: string) => void
}

// ── Constants ─────────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export const WISHLIST_QUERY_KEY = ['wishlist'] as const

// ── Context ──────────────────────────────────────────────────────────────────

const WishlistContext = createContext<WishlistContextValue | null>(null)

// eslint-disable-next-line react-refresh/only-export-components
export function useWishlist(): WishlistContextValue {
  const ctx = useContext(WishlistContext)
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider')
  return ctx
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function WishlistProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()

  const { data: items = [], isLoading } = useQuery({
    queryKey: WISHLIST_QUERY_KEY,
    queryFn: fetchWishlist,
    enabled: isAuthenticated,
    throwOnError: false,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
  })

  const addMutation = useMutation({
    mutationFn: (mpn: string) => addToWishlist(mpn),
    onMutate: async (mpn: string) => {
      await queryClient.cancelQueries({ queryKey: WISHLIST_QUERY_KEY })
      const previous = queryClient.getQueryData<WishlistItem[]>(WISHLIST_QUERY_KEY)
      // Optimistically add a placeholder so the heart fills immediately
      queryClient.setQueryData<WishlistItem[]>(WISHLIST_QUERY_KEY, prev => [
        ...(prev ?? []),
        {
          uuid: `optimistic-${mpn}`,
          normalizedMpn: mpn,
          addedAt: new Date().toISOString(),
          slug: '',
          displayTitle: mpn,
          primaryImageUrl: null,
          effectivePrice: null,
          effectivePriceIncVat: null,
          stockStatus: 'unknown',
        },
      ])
      return { previous }
    },
    onError: (_err, _mpn, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(WISHLIST_QUERY_KEY, ctx.previous)
      }
    },
    onSuccess: (newItem) => {
      queryClient.setQueryData<WishlistItem[]>(WISHLIST_QUERY_KEY, prev =>
        (prev ?? []).map(i => i.normalizedMpn === newItem.normalizedMpn ? newItem : i)
      )
    },
  })

  const removeMutation = useMutation({
    mutationFn: (uuid: string) => removeFromWishlist(uuid),
    onMutate: async (uuid: string) => {
      await queryClient.cancelQueries({ queryKey: WISHLIST_QUERY_KEY })
      const previous = queryClient.getQueryData<WishlistItem[]>(WISHLIST_QUERY_KEY)
      queryClient.setQueryData<WishlistItem[]>(WISHLIST_QUERY_KEY, prev =>
        (prev ?? []).filter(i => i.uuid !== uuid)
      )
      return { previous }
    },
    onError: (_err, _uuid, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(WISHLIST_QUERY_KEY, ctx.previous)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WISHLIST_QUERY_KEY })
    },
  })

  const isInWishlist = useCallback(
    (normalizedMpn: string) => items.some(i => i.normalizedMpn === normalizedMpn),
    [items],
  )

  const addItem = useCallback(
    (mpn: string) => addMutation.mutate(mpn),
    [addMutation],
  )

  const removeItem = useCallback(
    (uuid: string) => removeMutation.mutate(uuid),
    [removeMutation],
  )

  return (
    <WishlistContext.Provider value={{ items, isLoading, isInWishlist, addItem, removeItem }}>
      {children}
    </WishlistContext.Provider>
  )
}
